import React from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { iconType } from '../../utils';
import { useAuth } from '../../App';
import { storeService } from '../../data';
import { navigateForStores, withStores } from '../../utils/storeRouting';
import { log } from '../../utils/logger';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 60vh;
  padding: 2rem;
  text-align: center;
`;

const Icon = styled(FontAwesomeIcon)`
  font-size: 4rem;
  color: #C5A572;
  margin-bottom: 1.5rem;
`;

const Title = styled.h1`
  color: #4A3C31;
  margin-bottom: 1rem;
`;

const Message = styled.p`
  color: #2C1810;
  font-size: 1.1rem;
  max-width: 500px;
  line-height: 1.6;
`;

const RefreshButton = styled.button`
  margin-top: 1.5rem;
  background-color: #C5A572;
  color: white;
  border: none;
  padding: 12px 20px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 1rem;

  &:hover {
    background-color: #E8D5B5;
  }

  &:disabled {
    background-color: #E8D5B5;
    cursor: not-allowed;
  }
`;

const ErrorText = styled.p`
  color: #FF6B6B;
  margin-top: 1rem;
`;

const NoStores = () => {
  const navigate = useNavigate();
  const { user, stores, setUser, setStores } = useAuth();
  const [isRefreshing, setIsRefreshing] = React.useState(false);
  const [refreshError, setRefreshError] = React.useState('');

  const resolvedStores = React.useMemo(() => {
    if (Array.isArray(stores) && stores.length > 0) return stores;
    if (user?.stores && Array.isArray(user.stores)) return user.stores;
    return [];
  }, [stores, user]);

  // Safety net: leave /no-stores once stores are hydrated in context
  React.useEffect(() => {
    if (resolvedStores.length > 0) {
      navigateForStores(navigate, resolvedStores);
    }
  }, [resolvedStores, navigate]);

  const handleRefresh = async () => {
    setRefreshError('');
    setIsRefreshing(true);
    try {
      const fetchedStores = await storeService.getStores();
      const list = Array.isArray(fetchedStores) ? fetchedStores : [];
      setStores(list);
      setUser((prev) => withStores(prev || user || {}, list));
      navigateForStores(navigate, list);
    } catch (err) {
      log('Failed to refresh stores on NoStores page:', err);
      setRefreshError('Unable to refresh stores. Please try again.');
    } finally {
      setIsRefreshing(false);
    }
  };

  return (
    <Container>
      <Icon icon={iconType.Store} />
      <Title>No Stores Found</Title>
      <Message>
        We couldn't find any stores associated with your account. If you believe this is an error,
        please contact your store administrator or our support team.
      </Message>
      <RefreshButton type="button" onClick={handleRefresh} disabled={isRefreshing}>
        {isRefreshing ? 'Refreshing...' : 'Refresh stores'}
      </RefreshButton>
      {refreshError && <ErrorText>{refreshError}</ErrorText>}
    </Container>
  );
};

export default NoStores;
